import { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp } from "lucide-react";
import HomePage from "./pages/Home";
import "./App.css";

//Inventory
import CarInventory from "./pages/Inventory/CarInventory";

//Vehicle Details
import VehicleDetails from "./pages/VehicleDetails/VehicleDetails";

//Comparison Details
import CompareCars from "./pages/Compare/CompareCars";

// Finance Center
import Finance from "./pages/Finance/FinanceCenter";

//About US
import About from "./pages/About/About";

//Contact Us
import Contact from "./pages/Contact/Contact";

//Directions
import DirectionsPage from "./pages/Directions/Directions";

// Privacy Pages
import PrivacyPolicy from "./pages/Privacy/PrivacyPolicy";
import TermsConditions from "./pages/Privacy/TermsConditions";

//Navbar and Footer
import Navbar from "./components/ui/Navbar";
import Footer from "./components/ui/Footer";

function App() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  const isInventoryPage = location.pathname === "/car-inventory";

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[100] bg-gray-900 flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="text-center"
            >
              <div className="font-serif text-4xl md:text-6xl font-bold mb-4">
                <span className="text-brand-red">BIG BOYS</span>
                <span className="text-white"> MOTOR</span>
                <span className="text-brand-gold"> CLUB</span>
              </div>
              <motion.div
                className="w-48 h-1 bg-gradient-to-r from-brand-red via-brand-gold to-brand-red mx-auto rounded-full"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
              <p className="text-gray-400 mt-4 text-sm tracking-widest uppercase">
                Loading Experience...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-white w-full overflow-x-hidden">
        <Navbar />
        {/* Main Page Content */}
        <Routes location={location} key={location.pathname + location.hash}>
          <Route path="/" element={<HomePage />} />
          <Route path="/car-inventory" element={<CarInventory />} />
          <Route path="/vehicle-details/:id" element={<VehicleDetails />} />
          <Route path="/compare" element={<CompareCars />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/direction" element={<DirectionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
        </Routes>
        <Footer />

        {/* Scroll to Top Button */}
        <AnimatePresence>
          {showScrollTop && !isInventoryPage && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToTop}
              className="fixed bottom-8 right-8 z-40 w-14 h-14 bg-brand-red text-white rounded-full shadow-glow-red flex items-center justify-center hover:bg-red-700 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronUp className="w-6 h-6" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default App;
